import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useDismiss } from '../lib/useDismiss'

/**
 * A searchable multi-select popover — the app's first menu, and its first
 * keyboard-navigable control of any kind.
 *
 * ── WHY THIS REPLACES `<select multiple>` ───────────────────────────────────
 *
 * 046 shipped the admin filters as native multi-selects with a note underneath
 * telling people to ctrl-click. Three things are wrong with that and none of
 * them is taste:
 *
 *   1. There are 69 org units. A `size={6}` box shows six of them and scrolls,
 *      so finding "Santiago" means scrolling a list you cannot search while
 *      holding a modifier key you must not release.
 *   2. This hub is used on tablets — `--tap-min` is 44px precisely because of
 *      that, and it is written into the people table's own comment. **There is
 *      no ctrl key on a tablet**, so on the devices half the crews use, the
 *      multi-select was a single-select with a misleading note beneath it.
 *   3. Ctrl-click destroys a selection on a mis-click. One plain click on a
 *      six-item list clears the other five with no undo.
 *
 * So selection is a click that toggles and the popover STAYS OPEN, which is
 * what David was pointing at with Notion. That does contradict "filters should
 * always be ctrl+select for multiple" — his own clause was "unless otherwise
 * specified or it doesn't make sense", and both halves apply. Recorded here
 * rather than assumed: if he wants the modifier back, this component is where
 * it goes and the tablet problem is what has to be answered.
 *
 * ── ONE FLOATING SURFACE, NOT A SECOND ──────────────────────────────────────
 *
 * `.bellpanel` was the only popover in the app and it carried an undefined
 * `--shadow-menu` with a hard-coded fallback heavier than the design system's
 * own answer. Both surfaces are now `--shadow-md` — the step the elevation card
 * labels "md · menus" — with the same hairline, radius and z-index. A third
 * menu shadow is how a design system stops being one.
 *
 * ── KEYBOARD, BECAUSE THERE WAS NONE ANYWHERE ───────────────────────────────
 *
 * Nothing in `web/src` handled a key before this: no Escape, no arrows, no
 * focus return, no outside-click. That is fine for buttons and links, which the
 * browser already handles, and not fine for a thing that opens over the page —
 * a popover with no Escape is a trap, and one that does not return focus loses
 * a keyboard user's place entirely.
 *
 * The pattern is the standard combobox one: focus stays in the search box and
 * `aria-activedescendant` moves the highlight, so typing and navigating are the
 * same gesture. Rows are `role="option"` inside `aria-multiselectable`, which is
 * what tells a screen reader that picking one does not dismiss the rest.
 *
 * Rows are at the 44px floor. A 32px menu row is the modern-looking default and
 * it is the wrong one here for the same reason the 13px checkbox was.
 */
export type PickerOption = {
  value: string
  label: string
  /** Indentation depth, so a BU reads as sitting under its legal entity. */
  depth?: number
  /**
   * "ConeTec Chile › Santiago". Searched as well as the label — typing the
   * entity finds the units beneath it — and shown under the label once a query
   * has broken the tree apart, because an indented row with its parent filtered
   * away is indentation pointing at nothing.
   */
  hint?: string
  /**
   * A count shown at the end of the row ("12"), e.g. how many people hold a job
   * title. A fact about the option, never part of its name, so it is neither
   * searched nor repeated in the summary.
   */
  count?: number
  /**
   * Values of this row's ancestors in a tree (the Where filter: country →
   * entity → business unit). When one of them is chosen this row is already
   * included, so it shows a muted tick — choosing Canada and then wondering
   * whether Vancouver is in is the question this answers.
   */
  ancestors?: string[]
}

/**
 * Which options a query leaves, matching the HINT as well as the label.
 *
 * Exported because it is the half of this component a test can reach: the test
 * suite renders to a string with no DOM, so the popover's open state is not
 * observable there and this logic would otherwise be covered by nothing. It is
 * also where the interesting mistake lives — matching only `label` means typing
 * "Chile" finds the legal entity and none of the business units under it, which
 * looks like a working search returning the wrong answer.
 */
export function matchOptions(options: PickerOption[], query: string): PickerOption[] {
  const q = query.trim().toLowerCase()
  if (!q) return options
  return options.filter(
    (o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q),
  )
}

/**
 * What the trigger says. One name reads better than "1 selected" and is the
 * commonest case; past that a count is the only thing that fits.
 *
 * A value with no matching option falls back to the count rather than to the
 * raw id — an id on a filter button says nothing and looks like a fault.
 */
export function summarise(
  options: PickerOption[],
  selected: string[],
  anyLabel: string,
): string {
  if (selected.length === 0) return anyLabel
  if (selected.length === 1) {
    return options.find((o) => o.value === selected[0])?.label ?? '1 selected'
  }
  return `${selected.length} selected`
}

export function Picker({
  label,
  options,
  selected,
  onChange,
  anyLabel = 'Any',
  searchPlaceholder = 'Search',
  variant = 'field',
  className = '',
}: {
  label: string
  options: PickerOption[]
  selected: string[]
  onChange: (next: string[]) => void
  /** What the trigger says when nothing is chosen. "Anywhere", "Any role". */
  anyLabel?: string
  searchPlaceholder?: string
  /**
   * 'field' (default): a label above a full-width trigger — forms.
   * 'pill' (137): the label lives INSIDE a compact rounded trigger
   * ("Department ▾", then "Department · Engineering ▾") so a whole row of
   * filters fits on one line above a table. The visible label is still the
   * accessible name; only its position changes.
   */
  variant?: 'field' | 'pill'
  /** Extra class on the root, for a picker with its own panel width. */
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const baseId = useId()
  const listId = `${baseId}-list`
  const optionId = (i: number) => `${baseId}-opt-${i}`

  const chosen = useMemo(() => new Set(selected), [selected])

  const visible = useMemo(() => matchOptions(options, query), [options, query])

  // The highlight has to survive the list changing under it. Clamping rather
  // than resetting keeps the position when one row disappears; resetting to 0
  // on every keystroke would fight somebody who typed, arrowed down, then
  // corrected a letter.
  useEffect(() => {
    setActive((i) => Math.min(Math.max(i, 0), Math.max(visible.length - 1, 0)))
  }, [visible.length])

  useEffect(() => {
    if (!open) return
    inputRef.current?.focus()
  }, [open])

  // Escape and outside-click, from the one hook the bell panel also uses.
  // Escape returns focus to the trigger; a click elsewhere does not, because
  // the click has already said where the person wants to be.
  useDismiss(wrapRef, open, (viaKeyboard) => close(viaKeyboard))

  // Keep the highlighted row on screen. Without this, arrowing past the sixth
  // of 69 org units highlights rows nobody can see.
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [open, active, visible])

  function close(returnFocus: boolean) {
    setOpen(false)
    setQuery('')
    if (returnFocus) triggerRef.current?.focus()
  }

  function toggle(value: string) {
    // Stays open. That is the whole difference from a `<select>`, and it is why
    // picking three business units is three clicks instead of three clicks plus
    // a modifier plus not slipping.
    onChange(chosen.has(value) ? selected.filter((v) => v !== value) : [...selected, value])
  }

  function onKeyDown(e: React.KeyboardEvent) {
    // Escape is not here — useDismiss owns it, so the bell panel and this
    // cannot drift into two behaviours for one key.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (visible.length === 0) return
      const step = e.key === 'ArrowDown' ? 1 : -1
      // Wraps. A list of 69 where Up at the top does nothing feels broken; a
      // list where it goes to the bottom is how every menu behaves.
      setActive((i) => (i + step + visible.length) % visible.length)
      return
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      setActive(e.key === 'Home' ? 0 : Math.max(visible.length - 1, 0))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const o = visible[active]
      if (o) toggle(o.value)
      return
    }
    if (e.key === 'Tab') {
      // Tab is "I am done here", not "go to the search box's next sibling".
      // Closing without stealing focus back lets it land where the user aimed.
      close(false)
    }
  }

  const summary = summarise(options, selected, anyLabel)

  return (
    <div
      className={`picker${variant === 'pill' ? ' picker--pill' : ''}${className ? ` ${className}` : ''}`}
      ref={wrapRef}
      onKeyDown={onKeyDown}
    >
      <span
        className={variant === 'pill' ? 'visually-hidden' : 'admin__searchlabel'}
        id={`${baseId}-label`}
      >
        {label}
      </span>
      <button
        type="button"
        ref={triggerRef}
        className={`picker__trigger${selected.length ? ' picker__trigger--set' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${baseId}-label ${baseId}-summary`}
        onClick={() => (open ? close(true) : setOpen(true))}
      >
        {variant === 'pill' ? (
          <span id={`${baseId}-summary`} className="picker__summary">
            <span aria-hidden="true">{label}</span>
            {selected.length > 0 && (
              <span className="picker__pillvalue">
                {selected.length === 1 ? summary : selected.length}
              </span>
            )}
          </span>
        ) : (
          <span id={`${baseId}-summary`} className="picker__summary">
            {summary}
          </span>
        )}
        <span className="picker__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="picker__panel">
          <div className="picker__search">
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={visible.length ? optionId(active) : undefined}
              aria-label={`Search ${label.toLowerCase()}`}
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />
          </div>

          <ul className="picker__list" id={listId} role="listbox" aria-multiselectable="true" ref={listRef}>
            {visible.length === 0 && (
              <li className="picker__none" role="presentation">
                Nothing matches “{query}”.
              </li>
            )}
            {visible.map((o, i) => {
              const isChosen = chosen.has(o.value)
              const covered = !isChosen && (o.ancestors ?? []).some((a) => chosen.has(a))
              return (
                <li
                  key={o.value}
                  id={optionId(i)}
                  role="option"
                  aria-selected={isChosen}
                  data-active={i === active}
                  data-depth={o.depth ?? 0}
                  className={`picker__opt${isChosen ? ' picker__opt--on' : ''}${covered ? ' picker__opt--covered' : ''}`}
                  style={query ? undefined : { paddingInlineStart: `calc(var(--space-3) + ${o.depth ?? 0} * var(--space-4))` }}
                  // mousedown, not click: click would fire after the input has
                  // blurred, and the blur is what a less careful version of this
                  // component uses to close itself.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    setActive(i)
                    toggle(o.value)
                  }}
                >
                  {/* A tick that is always in the layout, drawn or not, so
                      selecting a row does not shift the label under the cursor
                      and make the next click land somewhere else. */}
                  <span className="picker__tick" aria-hidden="true">
                    {isChosen || covered ? '✓' : ''}
                  </span>
                  <span className="picker__optlabel">
                    {o.label}
                    {/* Only once a query has broken the tree apart. Unfiltered,
                        the indentation already says this. */}
                    {query && o.hint && <span className="picker__hint">{o.hint}</span>}
                  </span>
                  {o.count !== undefined && <span className="picker__count">{o.count}</span>}
                </li>
              )
            })}
          </ul>

          {selected.length > 0 && (
            <div className="picker__foot">
              {/* Worded, not an ×. This clears the whole filter rather than
                  removing the label you are looking at — the same distinction
                  FilterChips draws between its × and its "Clear filters". */}
              <button type="button" className="btn btn--quiet" onClick={() => onChange([])}>
                Clear {label.toLowerCase()}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
